import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { t } from '../../../services/i18nService';
import utilService from '../../../services/UtilService';
import navigationService from '../../../services/NavigationService';
import styles from './annotationItem.scss';
import DeleteButtons from './DeleteButtons';
import annotationService from '../../../services/AnnotationService';
import NoteInput from './NoteInput';
import scorableItemNumberingService from '../../../services/questionView/ScorableItemNumberingService';
import UIConstants from '../../../constants/UIConstants';
import objectTypes from '../../../constants/ObjectTypes';

const AnnotationItem = ({ content, contentId, focused, id, number, scorableItem, sectionId, selector, type, comment = '' }) => {
    const itemRef = useRef();
    const isLongContent = content.length >= UIConstants.TRUNCATED_ANNOTATION_LENGTH;
    const [contentIsOpened, setContentIsOpened] = useState(!isLongContent);
    const [annotationItemFocus, setAnnotationItemFocus] = useState();

    const toggleContentVisibility = (event) => {
        setContentIsOpened(!contentIsOpened);
        event.stopPropagation();
    };

    const onAnnotationItemClick = () => {
        if (!focused) {
            annotationService.dispatchFocusAnnotationEvent(selector);
            annotationService.setFocusedAnnotation(selector);
        }

        if (scorableItemNumberingService.useNumberPerInteraction() && !isNaN(scorableItem)) {
            navigationService.navigateToScorableItem(sectionId, contentId, scorableItem, true);
        } else if (type === objectTypes.CATEGORY.STIMULUS) {
            navigationService.navigateToSection(sectionId);
        } else {
            navigationService.navigateToQuestion(contentId, sectionId, true);
        }
    };

    const getQuestionNumber = () => {
        return (number && number.length > 0)
            ? <span className={styles.questionNumber}>{number}</span>
            : null;
    };

    let className = styles.annotationItem;

    useEffect(() => {
        if (focused) {
            itemRef.current.scrollIntoView();
        }
    }, []);

    const onAnnotationItemFocus = () => {
        if (!annotationItemFocus) {
            setAnnotationItemFocus(true);
        }
    };

    const onAnnotationItemBlur = () => {
        if (annotationItemFocus) {
            setAnnotationItemFocus(false);
        }
    };

    if (annotationItemFocus || focused) {
        className += ` ${styles.annotationItemFocused}`;
    }

    return (
        /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
        <li
            className={className}
            ref={itemRef}
            onFocus={onAnnotationItemFocus}
            onBlur={onAnnotationItemBlur}
        >
            <button className={styles.annotationTextButton} onClick={onAnnotationItemClick} >
                {getQuestionNumber()}
                <span className={styles.annotationText}>
                    {contentIsOpened
                        ? content
                        : utilService.truncate(content, UIConstants.TRUNCATED_ANNOTATION_LENGTH)
                    }
                </span>
            </button>
            {isLongContent
                ? <button className={styles.linkTypeButton} onClick={toggleContentVisibility}>
                    {contentIsOpened
                        ? t('button.showLess')
                        : t('button.showMore')
                    }
                </button>
                : null
            }

            <NoteInput
                id={id}
                questionId={contentId}
                selector={selector}
                focused={focused}
                comment={comment}
            />
            <DeleteButtons id={id} questionNumber={number} questionId={contentId} selector={selector} type={type} />
        </li>
    );
};

AnnotationItem.propTypes = {
    comment: PropTypes.string,
    content: PropTypes.string.isRequired,
    contentId: PropTypes.number.isRequired,
    focused: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    number: PropTypes.string,
    scorableItem: PropTypes.number,
    sectionId: PropTypes.number.isRequired,
    selector: PropTypes.array.isRequired,
    type: PropTypes.string,
};

function areBothObjects(a, b) {
    return a && b && typeof a === 'object' && typeof b === 'object';
}

function areArraysEqual(arrA, arrB) {
    if (arrA.length !== arrB.length) return false;
    return arrA.every((item, i) => deepEqual(item, arrB[i]));
}

function areObjectsEqual(objA, objB) {
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) return false;

    return keysA.every(
        key => Object.hasOwn(objB, key) && deepEqual(objA[key], objB[key])
    );
}

export function deepEqual(a, b) {
    if (a === b) return true;

    if (areBothObjects(a, b)) {
        if (a.constructor !== b.constructor) return false;
        if (Array.isArray(a)) return areArraysEqual(a, b);
        return areObjectsEqual(a, b);
    }

    return Number.isNaN(a) && Number.isNaN(b);
}

export function arePropsEqual(prev, next) {
    return (
        prev.comment === next.comment &&
        prev.content === next.content &&
        prev.contentId === next.contentId &&
        prev.focused === next.focused &&
        prev.id === next.id &&
        prev.number === next.number &&
        prev.scorableItem === next.scorableItem &&
        prev.sectionId === next.sectionId &&
        prev.type === next.type &&
        deepEqual(prev.selector, next.selector)
    );
}

export default React.memo(AnnotationItem, arePropsEqual);
